import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Client } from "../../types";
import { NAVIGATION, ROLES } from "../../constants";
import { useClients } from "../../hooks/useClients";
import { useNavigationState } from "../../hooks/useNavigationState";
import { formatDate } from "../../lib/formatDate";
import DataTable from "../../components/tables/DataTable";
import FilesColumn from "../../components/files/FilesColumn";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { useAuth } from "../../contexts/AuthContext";

const Clients: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state: navState, updateState } = useNavigationState();
  const [tabValue, setTabValue] = useState(navState.tab || 0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const isAdmin = user?.role === ROLES.ADMIN;

  // Restore navigation state when component mounts
  useEffect(() => {
    if (navState.tab !== undefined) {
      setTabValue(navState.tab);
    }
  }, [navState.tab]);

  const { clients, isLoading, error, deleteClient, isDeleting } = useClients();

  const activeClients = clients.filter((client: Client) => client.isActive);
  const inactiveClients = clients.filter((client: Client) => !client.isActive);
  const currentClients = tabValue === 0 ? activeClients : inactiveClients;

  const getColumns = (isInactive: boolean): GridColDef<Client>[] => {
    const baseColumns: GridColDef<Client>[] = [
      {
        field: "id",
        headerName: "#",
        width: 15,
        filterable: false,
        sortable: true,
        valueFormatter: (value: number, row: Client) =>
          value ? currentClients.indexOf(row) + 1 : 0,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        filterable: true,
        sortable: true,
      },
      {
        field: "startDate",
        headerName: "Start Date",
        flex: 1,
        filterable: true,
        sortable: true,
        valueFormatter: (value: any) => (value ? formatDate(value) : ""),
      },
      {
        field: "address",
        headerName: "Address",
        flex: 1.5,
        filterable: true,
        sortable: true,
      },
      {
        field: "zipCode",
        headerName: "Zip Code",
        flex: 1,
        filterable: true,
        sortable: true,
      },
      {
        field: "city",
        headerName: "City",
        flex: 1,
        filterable: true,
        sortable: true,
      },
      {
        field: "country",
        headerName: "Country",
        flex: 1,
        filterable: true,
        sortable: true,
      },
      {
        field: "files",
        headerName: "Files",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <FilesColumn entityType="CLIENT" entityId={params.row.id} />
        ),
      },
    ];

    // Add actions column for admins
    if (isAdmin) {
      baseColumns.push({
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="Delete Client">
            <IconButton
              size="small"
              color="error"
              onClick={(event: any) => {
                event.stopPropagation();
                setSelectedClient(params.row);
                setDeleteDialogOpen(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ),
      });
    }

    // Add termination date column only for inactive clients
    if (isInactive) {
      baseColumns.splice(6, 0, {
        field: "terminationDate",
        headerName: "Termination Date",
        flex: 1,
        filterable: true,
        sortable: true,
        valueFormatter: (value: any) => (value ? formatDate(value) : ""),
      });
    }

    return baseColumns;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    updateState({ tab: newValue, page: 0 }); // Reset page when changing tabs
  };

  const handleDeleteClient = () => {
    if (selectedClient) {
      deleteClient(selectedClient.id);
      setDeleteDialogOpen(false);
      setSelectedClient(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedClient(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Clients
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(NAVIGATION.ADD_CLIENT)}
        >
          Add Client
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label={`Active Clients (${activeClients.length})`} />
        <Tab label={`Inactive Clients (${inactiveClients.length})`} />
      </Tabs>

      {error instanceof Error && (
        <Typography color="error">{error.message}</Typography>
      )}

      <DataTable
        rows={currentClients}
        columns={getColumns(tabValue === 1)}
        loading={isLoading}
        navigationPath={NAVIGATION.CLIENTS}
        initialPage={navState.page}
        onPageChange={(page) => updateState({ page })}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteClient}
        title="Delete Client"
        itemName={selectedClient?.name || ""}
        itemType="client"
        loading={isDeleting}
      />
    </Box>
  );
};

export default Clients;
